import { SectionFooter } from "@admin-ui/components/application/section-footers/section-footer";
import { SectionHeader } from "@admin-ui/components/application/section-headers/section-headers";
import { SectionLabel } from "@admin-ui/components/application/section-headers/section-label";
import { NativeSelect } from "@admin-ui/components/base/select/select-native";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Tab, TabList, TabPanel, Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import type { AdminDestinationsLocation } from "@travily/types/models";
import { RefreshCcw01 } from "@untitledui/icons";
import type { FormEvent, ReactNode } from "react";
import { TableBody } from "react-aria-components";
import type { BboxCoords } from "./components/bbox-rectangle";

function isInsideBbox(lat: number, lng: number, b: BboxCoords): boolean {
  return lat >= b.south && lat <= b.north && lng >= b.west && lng <= b.east;
}

export interface EditViewProps {
  defaultTab: string;
  onTabChange: (key: string) => void;

  name: string;
  countryCode: string;
  description: string;
  availableCountries: Array<{ iso_alpha2: string; name: string }>;
  processing: boolean;
  cancelHref: string;
  onNameChange: (value: string) => void;
  onCountryCodeChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onDetailsSubmit: (event: FormEvent) => void;

  photoSection: ReactNode;

  showResetBbox: boolean;
  onResetBbox: () => void;
  bboxEditorMap: ReactNode;

  bbox: BboxCoords;
  activeLocations: AdminDestinationsLocation[];
  excludedLocations: AdminDestinationsLocation[];
  locationsMap: ReactNode;
  addLocations: ReactNode;
  onExcludeLocation: (locationId: number) => void;
  onUnlinkLocation: (locationId: number) => void;
  onReincludeLocation: (locationId: number) => void;
}

export function EditView({
  defaultTab,
  onTabChange,
  name,
  countryCode,
  description,
  availableCountries,
  processing,
  cancelHref,
  onNameChange,
  onCountryCodeChange,
  onDescriptionChange,
  onDetailsSubmit,
  photoSection,
  showResetBbox,
  onResetBbox,
  bboxEditorMap,
  bbox,
  activeLocations,
  excludedLocations,
  locationsMap,
  addLocations,
  onExcludeLocation,
  onUnlinkLocation,
  onReincludeLocation,
}: EditViewProps) {
  return (
    <Tabs defaultSelectedKey={defaultTab} onSelectionChange={(key) => onTabChange(String(key))}>
      <TabList orientation="horizontal" type="button-minimal">
        <Tab id="details" label="Details" />
        <Tab id="locations" label="Locations" badge={String(activeLocations.length)} />
      </TabList>

      <TabPanel id="details">
        <form onSubmit={onDetailsSubmit} className="flex flex-col gap-6 pt-8">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
                <SectionHeader.Heading>Destination details</SectionHeader.Heading>
                <SectionHeader.Subheading>Update the destination info and matching boundary.</SectionHeader.Subheading>
              </div>
              <SectionHeader.Actions>
                <Button color="secondary" size="sm" href={cancelHref}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" size="sm" isDisabled={processing}>
                  Save
                </Button>
              </SectionHeader.Actions>
            </SectionHeader.Group>
          </SectionHeader.Root>

          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
              <SectionLabel.Root isRequired size="sm" title="Name" className="max-lg:hidden" />
              <Input label="Name" value={name} onChange={onNameChange} isRequired className="lg:label:hidden" />
            </div>

            <hr className="h-px w-full border-none bg-border-secondary" />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
              <SectionLabel.Root
                isRequired
                size="sm"
                title="Country"
                description="The country this destination belongs to"
                className="max-lg:hidden"
              />
              <NativeSelect
                label="Country"
                value={countryCode}
                onChange={(event) => onCountryCodeChange(event.target.value)}
                options={availableCountries.map((country) => ({
                  value: country.iso_alpha2,
                  label: country.name,
                }))}
                className="lg:label:hidden"
              />
            </div>

            <hr className="h-px w-full border-none bg-border-secondary" />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
              <SectionLabel.Root size="sm" title="Description" description="Brief description of this destination." />
              <textarea
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                className="w-full rounded-lg border border-primary px-3.5 py-2.5 text-sm text-primary shadow-xs placeholder:text-placeholder focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/12"
                rows={3}
                placeholder="Optional description"
              />
            </div>

            <hr className="h-px w-full border-none bg-border-secondary" />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
              <SectionLabel.Root
                size="sm"
                title="Photos"
                description="Photos for this destination. The first photo is the cover."
              />
              {photoSection}
            </div>

            <hr className="h-px w-full border-none bg-border-secondary" />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_1fr] lg:gap-8">
              <SectionLabel.Root
                size="sm"
                title="Bounding box"
                description="Drag the corners to adjust the area. Locations inside this boundary are automatically matched."
              >
                {showResetBbox && (
                  <SectionLabel.Actions>
                    <Button size="xs" color="tertiary" iconLeading={RefreshCcw01} onClick={onResetBbox}>
                      Reset to Mapbox default
                    </Button>
                  </SectionLabel.Actions>
                )}
              </SectionLabel.Root>
              {bboxEditorMap}
            </div>
          </div>

          <SectionFooter.Root>
            <SectionFooter.Actions>
              <Button color="secondary" size="sm" href={cancelHref}>
                Cancel
              </Button>
              <Button type="submit" color="primary" size="sm" isDisabled={processing}>
                Save
              </Button>
            </SectionFooter.Actions>
          </SectionFooter.Root>
        </form>
      </TabPanel>

      <TabPanel id="locations">
        <div className="flex flex-col gap-6 pt-8">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
                <SectionHeader.Heading>Linked locations</SectionHeader.Heading>
                <SectionHeader.Subheading>
                  Automatic matches come from the bounding box. Manually included locations persist regardless of the
                  boundary.
                </SectionHeader.Subheading>
              </div>
            </SectionHeader.Group>
          </SectionHeader.Root>

          {locationsMap}

          <TableCard.Root>
            <TableCard.Header title="Active locations" badge={`${activeLocations.length}`} />
            <Table aria-label="Active locations">
              <Table.Header>
                <Table.Head id="name" label="Name" isRowHeader />
                <Table.Head id="address" label="Address" />
                <Table.Head id="type" label="Type" />
                <Table.Head id="actions" label="" />
              </Table.Header>
              <TableBody items={activeLocations}>
                {(dl) => {
                  const inBbox = isInsideBbox(dl.location.latitude, dl.location.longitude, bbox);
                  return (
                    <Table.Row key={dl.id} id={dl.id}>
                      <Table.Cell>
                        <span className="text-sm font-medium text-primary">{dl.location.name}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-sm text-tertiary">{dl.location.full_address ?? "-"}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <BadgeWithDot color={inBbox ? "blue" : "success"} size="sm">
                          {inBbox ? "Auto-matched" : "Manually added"}
                        </BadgeWithDot>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center justify-end gap-3">
                          {inBbox ? (
                            <Button size="sm" color="link-gray" onClick={() => onExcludeLocation(dl.location.id)}>
                              Exclude
                            </Button>
                          ) : (
                            <Button size="sm" color="link-gray" onClick={() => onUnlinkLocation(dl.location.id)}>
                              Unlink
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                }}
              </TableBody>
            </Table>
          </TableCard.Root>

          {excludedLocations.length > 0 && (
            <TableCard.Root>
              <TableCard.Header
                title="Excluded locations"
                badge={`${excludedLocations.length}`}
                description="These locations fall within the bounding box but have been manually excluded."
              />
              <Table aria-label="Excluded locations">
                <Table.Header>
                  <Table.Head id="name" label="Name" isRowHeader />
                  <Table.Head id="address" label="Address" />
                  <Table.Head id="actions" label="" />
                </Table.Header>
                <TableBody items={excludedLocations}>
                  {(dl) => (
                    <Table.Row key={dl.id} id={dl.id}>
                      <Table.Cell>
                        <span className="text-sm font-medium text-tertiary">{dl.location.name}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-sm text-tertiary">{dl.location.full_address ?? "-"}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center justify-end gap-3">
                          <Button size="sm" color="link-color" onClick={() => onReincludeLocation(dl.location.id)}>
                            Re-include
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </TableBody>
              </Table>
            </TableCard.Root>
          )}

          {addLocations}
        </div>
      </TabPanel>
    </Tabs>
  );
}
